import { useState, useRef, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Card,
  Input,
  Checkbox,
  Radio,
  Button,
  message,
  Modal,
  Space,
  Typography,
  Divider,
  Row,
  Col,
  Tag,
  Spin,
  Select,
  Tooltip
} from "antd";
import {
  FaCalendar,
  FaDownload,
  FaPhone,
  FaShieldAlt,
  FaPen,
  FaUndo,
  FaEnvelope,
  FaBarcode,
  FaCheckCircle,
  FaSearch,
  FaTimes,
  FaSchool,
  FaTools,
  FaUser,
  FaBox,
  FaBars
} from "react-icons/fa";
import { Html5QrcodeScanner, Html5Qrcode } from "html5-qrcode";
import DrawerComponent from "../Drawer";
import UserService from "../../utils/userService";
import { DRAWER_CONSTANTS } from "../../constants/schoolData";
import { EVENT_NAMES_CONSTANT, logFirebaseEvent } from "../../services/events";
import "./ReportGenerator.css";

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;

// ═══════════════════════════════════════════════════════════════
// HOOK: useFormPersistence (inline) - Modified to clear on reload
// ═══════════════════════════════════════════════════════════════

const STORAGE_KEY = 'installation-report-data';

function useFormPersistence(initialData) {
  const [data, setData] = useState(initialData);

  useEffect(() => {
    try {
      // Clear any existing data on component mount (page reload)
      localStorage.removeItem(STORAGE_KEY);
      // Start with fresh initial data
      setData(initialData);
    } catch (error) {
      console.error('Error clearing data from localStorage:', error);
    }
  }, []); // Empty dependency array means this runs only on mount

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving data to localStorage:', error);
    }
  }, [data]);

  const clearSavedData = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing data from localStorage:', error);
    }
  };

  return [data, setData, clearSavedData];
}

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENT: BarcodeScanner (inline)
// ═══════════════════════════════════════════════════════════════

const BarcodeScanner = ({ visible, onScan, onClose }) => {
  const scannerRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const isInitializing = useRef(false);

  const stopScanner = async () => {
    if (scannerRef.current && isScanning) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
        scannerRef.current = null;
        setIsScanning(false);
      } catch (err) {
        // Ignore errors when stopping scanner that's already stopped
        console.warn('Scanner stop error (ignored):', err.message);
        scannerRef.current = null;
        setIsScanning(false);
      }
    }
  };

  useEffect(() => {
    if (!visible) {
      // Clean up when modal closes
      stopScanner();
      setError(null);
      isInitializing.current = false;
      return;
    }

    // Prevent multiple initializations
    if (isInitializing.current || scannerRef.current) {
      return;
    }

    // Wait for DOM to be ready
    const timer = setTimeout(() => {
      const element = document.getElementById("barcode-reader");
      
      if (!element) {
        setError("Scanner element not found. Please try again.");
        return;
      }

      if (!scannerRef.current && !isInitializing.current) {
        isInitializing.current = true;
        
        try {
          const html5QrCode = new Html5Qrcode("barcode-reader");
          
          const config = {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            formatsToSupport: [
              5,  // CODE_128
              3,  // CODE_39
              4,  // CODE_93
              9,  // EAN_13
              10, // EAN_8
              14, // UPC_A
              15, // UPC_E
              8,  // ITF
              0,  // QR_CODE
              6,  // DATA_MATRIX
            ],
          };

          html5QrCode.start(
            { facingMode: "environment" },
            config,
            (decodedText, decodedResult) => {
              // Success callback
              stopScanner().then(() => {
                onScan(decodedText);
              });
            },
            (errorMessage) => {
              // Error callback - ignore NotFoundException
            }
          ).then(() => {
            scannerRef.current = html5QrCode;
            setIsScanning(true);
            isInitializing.current = false;
          }).catch((err) => {
            isInitializing.current = false;
            let errorMessage = "Failed to start camera. ";
            
            if (err.name === 'NotAllowedError' || (err.message && err.message.includes('Permission'))) {
              errorMessage += "Camera permission denied. Please allow camera access in your browser settings.";
            } else if (err.name === 'NotFoundError') {
              errorMessage += "No camera found on this device.";
            } else if (err.name === 'NotReadableError') {
              errorMessage += "Camera is already in use by another application.";
            } else if (err.name === 'NotSupportedError' || (err.message && err.message.includes('HTTPS'))) {
              errorMessage += "Camera access requires HTTPS or localhost.";
            } else {
              errorMessage += err.message || "Please check camera permissions and try again.";
            }
            
            setError(errorMessage);
          });
        } catch (err) {
          isInitializing.current = false;
          setError(`Failed to initialize scanner: ${err.message || 'Unknown error'}`);
        }
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      stopScanner();
    };
  }, [visible, onScan]);

  const handleClose = () => {
    stopScanner().then(() => {
      setError(null);
      onClose();
    });
  };

  if (!visible) return null;

  return (
    <Modal
      title={
        <Space>
          <span style={{ fontSize: 20 }}>📷</span>
          <span>Scan Barcode</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="close" onClick={handleClose}>
          Close Scanner
        </Button>
      ]}
      width="90%"
      style={{ maxWidth: 600 }}
      destroyOnClose={false}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {error ? (
          <div style={{
            padding: 20,
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: 8,
            textAlign: 'center'
          }}>
            <Paragraph style={{ color: '#dc2626', margin: 0 }}>
              {error}
            </Paragraph>
            <Button 
              type="primary" 
              onClick={() => {
                setError(null);
                stopScanner().then(() => {
                  onClose();
                });
              }}
              style={{ marginTop: 16 }}
            >
              Try Again
            </Button>
          </div>
        ) : (
          <>
            <div 
              id="barcode-reader" 
              style={{ 
                width: '100%',
                minHeight: 300
              }}
            />
            
            <div style={{
              padding: 16,
              background: '#e0f2fe',
              border: '1px solid #7dd3fc',
              borderRadius: 8
            }}>
              <Paragraph style={{ margin: 0, fontSize: 14, color: '#0369a1' }}>
                <strong>📱 Instructions:</strong>
                <br />
                • Allow camera permission when prompted by your browser
                <br />
                • Position the barcode within the scanning frame
                <br />
                • Ensure good lighting for best results
                <br />
                • Hold steady until the barcode is detected
                <br />
                • Supports QR codes, barcodes, and serial numbers
              </Paragraph>
            </div>
            
            <div style={{
              padding: 12,
              background: '#fef3c7',
              border: '1px solid #fbbf24',
              borderRadius: 8,
              fontSize: 12
            }}>
              <Text style={{ color: '#92400e' }}>
                <strong>⚠️ Note:</strong> Camera access requires HTTPS or localhost. 
                If camera doesn't open, check browser permissions or console for errors.
              </Text>
            </div>
          </>
        )}
      </Space>
    </Modal>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT: InstallationReportStandalone
// ═══════════════════════════════════════════════════════════════

const InstallationReportStandalone = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isLoggedIn = UserService.isLoggedIn();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [drawerState, setDrawerState] = useState({
    isOpen: false,
    menuItemSelected: "",
  });

  const initialFormData = {
    installationDate: null,
    schoolName: "",
    spocName: "",
    spocRole: "",
    spocEmail: "",
    schoolAddress: "",
    salesOrderNo: "",
    totalIFPQty: "",
    brandName: "",
    dealId: "",
    nucleusId: "",
    serialNumbers: [],
    upsPresent: "",
    earthingRequired: "",
    installationType: "",
    accessories: {
      stylus: true,
      remote: true,
      powerCable: true,
      touchCable: true,
      hdmiCable: true,
    },
    quickCheck: Array(16).fill(true),
    engineerName: "",
    engineerContact: "",
    schoolOwnerEmail: "",
    digitalSignature: "",
    mobileNumber: "",
    otp: "",
    agreement: false,
    installationComplete: false,
    // New fields for contact selection
    selectedContact: null,
    signaturePerson: "",
    signatureRole: "",
    contacts: [],
  };

  const [formData, setFormData, clearSavedData] = useFormPersistence(initialFormData);

  const [tokenState, setTokenState] = useState({ token: null, isValidating: false, isValid: false, error: null });

  useEffect(() => {
    let ignore = false;
    const token = searchParams.get('token');
    if (!token) {
      navigate('/');
      return;
    }
    setTokenState({ token, isValidating: true, isValid: false, error: null });
    fetch(`${process.env.REACT_APP_GREENFIELD_URL}/api/installation-report/validate-link?token=${encodeURIComponent(token)}`)
      .then(res => res.json())
      .then(result => {
        if (ignore) return;
        if (result.success) {
          setTokenState({ token, isValidating: false, isValid: true, error: null });
          setFormData(prev => ({ ...prev, salesOrderNo: result.soNo }));
          fetchSchoolDetailsWithSoNo(result.soNo, token);
        } else if (result.reason === 'expired') {
          setTokenState({ token, isValidating: false, isValid: false, error: 'expired' });
        } else {
          navigate('/');
        }
      })
      .catch(() => { if (!ignore) navigate('/'); });
    return () => { ignore = true; };
  }, []);

  const [schoolDataState, setSchoolDataState] = useState({
    isLoading: false,
    isFetched: false,
    error: null,
  });

  const [otpState, setOtpState] = useState({
    isOtpSent: false,
    isOtpVerified: false,
    isLoading: false,
    countdown: 0,
  });

  const [signatureState, setSignatureState] = useState({
    isSigned: false,
    signatureData: "",
  });

  const [scannerVisible, setScannerVisible] = useState(false);
  const [signatureModalVisible, setSignatureModalVisible] = useState(false);
  const [qcModalVisible, setQcModalVisible] = useState(false);
  const [failedItems, setFailedItems] = useState([]);
  const [accessoriesModalVisible, setAccessoriesModalVisible] = useState(false);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(null);
  const [deviceTypeModalVisible, setDeviceTypeModalVisible] = useState(false);
  const [pendingSerialNumber, setPendingSerialNumber] = useState("");
  const [currentDeviceQcChecks, setCurrentDeviceQcChecks] = useState([]);
  const [manualEntryModalVisible, setManualEntryModalVisible] = useState(false);
  const [manualSerialNumber, setManualSerialNumber] = useState("");
  const [productPhoto, setProductPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Device-specific QC checks
  const tvQcChecks = [
    "Power on/off functionality",
    "Display quality",
    "Audio output",
    "Remote control functionality",
    "Input ports verification",
    "Network connectivity (if applicable)",
    "Mounting stability",
    "Cable management"
  ];

  const ifpdQcChecks = [
    "Power on/off functionality",
    "Display quality",
    "Touch functionality",
    "Audio output",
    "Input ports verification",
    "Network connectivity",
    "Software/firmware version",
    "Interactive features (pen, touch gestures)",
    "Mounting stability",
    "Cable management"
  ];

  // Device-specific accessories
  const tvAccessories = [
    { key: 'remote', label: 'Remote Control' },
    { key: 'powerCable', label: 'Power Cable' },
    { key: 'hdmiCable', label: 'HDMI Cable' },
    { key: 'mountingBracket', label: 'Mounting Bracket' },
    { key: 'userManual', label: 'User Manual' }
  ];

  const ifpdAccessories = [
    { key: 'stylus', label: 'Stylus (2N)' },
    { key: 'remote', label: 'Remote Control' },
    { key: 'powerCable', label: 'Power Cable' },
    { key: 'touchCable', label: 'Touch Cable' },
    { key: 'hdmiCable', label: 'HDMI Cable' },
    { key: 'mountingBracket', label: 'Mounting Bracket' },
    { key: 'userManual', label: 'User Manual' },
    { key: 'calibrationTool', label: 'Calibration Tool' }
  ];

  // Initialize canvas for signature
  useEffect(() => {
    if (signatureModalVisible && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        if (signatureState.isSigned && signatureState.signatureData) {
          const img = new Image();
          img.onload = () => {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          };
          img.src = signatureState.signatureData;
        }
      }
    }
  }, [signatureModalVisible, signatureState.signatureData]);

  // Handle window resize for responsive drawer
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Drawer functions
  const handleSetDrawerState = (type, value = "") => {
    if (type === DRAWER_CONSTANTS.drawerState) {
      setDrawerState((prev) => ({
        isOpen: !prev.isOpen,
        menuItemSelected: "",
      }));
      return;
    }
    setDrawerState({ ...drawerState, menuItemSelected: value });
  };

  const handleLogOut = () => {
    // Firebase log events
    logFirebaseEvent(EVENT_NAMES_CONSTANT.logout_clicked, {
      user_id: UserService.getUserData()?.id,
    });
    UserService.handleLogout();
  };

  const handleAuthError = (response, result) => {
    if (response.status !== 401) return false;
    if (result.message === 'Link has expired') {
      setTokenState(prev => ({ ...prev, isValid: false, error: 'expired' }));
    } else {
      navigate('/');
    }
    return true;
  };

  const fetchSchoolDetailsWithSoNo = (soNo, token) => fetchSchoolDetails(soNo, token);

  const fetchSchoolDetails = async (overrideSoNo, overrideToken) => {
    const soNo = overrideSoNo || formData.salesOrderNo;
    const token = overrideToken || tokenState.token;
    if (!soNo.trim()) {
      message.warning("Please enter a sales order number first");
      return;
    }
    setSchoolDataState({ isLoading: true, isFetched: false, error: null });

    try {
      // Use backend proxy to fetch order details by sales order number
      const response = await fetch(
        `${process.env.REACT_APP_GREENFIELD_URL}/api/oms/order-details?soNo=${soNo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = await response.json();

      if (handleAuthError(response, result)) return;
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to fetch order details');
      }

      
      const orderData = result.data;
      
      if (!orderData || !orderData.data || !orderData.data.devices || orderData.data.devices.length === 0) {
        throw new Error('No order details found for this sales order number');
      }

      // Extract school information from the first device entry
      const firstDevice = orderData.data.devices[0];
      
      // Count total IFP/TV devices only (not TAB)
      const ifpTvDevices = orderData.data.devices.filter(device => 
        device.deviceType === 'IFP' || device.deviceType === 'TV'
      );
      const totalIFPQty = ifpTvDevices.reduce((sum, device) => sum + (device.qty || 0), 0);

      // Extract contacts and find school owner email
      const contacts = orderData.data.contacts || [];
      const schoolOwner = contacts.find(contact => contact.type === 'SCHOOL_OWNER');
      const schoolOwnerEmail = schoolOwner ? schoolOwner.email : '';

      // Format school address from the structured address object
      let formattedAddress = '';
      if (orderData.data.schoolAddress && typeof orderData.data.schoolAddress === 'object') {
        const addr = orderData.data.schoolAddress;
        const addressParts = [
          addr.address,
          addr.city,
          addr.district,
          addr.state,
          addr.pinCode
        ].filter(part => part && part.trim()); // Remove empty parts
        formattedAddress = addressParts.join(', ');
      } else if (orderData.data.schoolAddress) {
        // Fallback for string format
        formattedAddress = orderData.data.schoolAddress;
      } else if (firstDevice.schoolAddress) {
        formattedAddress = firstDevice.schoolAddress;
      }

      setFormData(prev => ({
        ...prev,
        schoolName: firstDevice.schoolName || "",
        spocName: orderData.data.spocName || "",
        schoolAddress: formattedAddress,
        totalIFPQty: totalIFPQty.toString(),
        brandName: orderData.data.brand || firstDevice.series || "",
        dealId: orderData.data.dealId || "",
        nucleusId: firstDevice.nucleusId || "",
        contacts: contacts,
        schoolOwnerEmail: schoolOwnerEmail,
      }));
      setSchoolDataState({ isLoading: false, isFetched: true, error: null });
      message.success("Order details fetched successfully");
    } catch (error) {
      console.error('Error fetching order details:', error);
      setSchoolDataState({ 
        isLoading: false, 
        isFetched: false, 
        error: error.message || 'Failed to fetch order details' 
      });
      message.error(error.message || 'Order not found. Please check the sales order number.');
    }
  };

  // Handle contact selection for signature and OTP
  const handleContactSelection = (contactId) => {
    const selectedContact = formData.contacts.find(contact => contact.email === contactId);
    if (selectedContact) {
      setFormData({
        ...formData,
        selectedContact: selectedContact,
        signaturePerson: selectedContact.name,
        signatureRole: selectedContact.type === 'SCHOOL_OWNER' ? 'School Owner' : 
                      selectedContact.type === 'AM' ? 'Account Manager' :
                      selectedContact.type === 'CH' ? 'Cluster Head' :
                      selectedContact.type === 'ZH' ? 'Zone Head' : selectedContact.type,
        mobileNumber: selectedContact.phoneNumber || '',
      });
      
      // Reset OTP state when contact changes
      setOtpState({
        isOtpSent: false,
        isOtpVerified: false,
        isLoading: false,
        countdown: 0,
      });
      
      message.success(`Selected ${selectedContact.name} for signature and OTP verification`);
    }
  };

  const removeSerialNumber = (index) => {
    const newSerialNumbers = formData.serialNumbers.filter((_, i) => i !== index);
    setFormData({ ...formData, serialNumbers: newSerialNumbers });
    message.success("Serial number removed");
  };

  const handleScanResult = (result) => {
    setScannerVisible(false);
    const serialValue = result.trim();
    const isDuplicate = formData.serialNumbers.some(item =>
      typeof item === 'string' ? item === serialValue : item.serial === serialValue
    );
    const ifpQty = parseInt(formData.totalIFPQty) || 0;
    const isWithinLimit = ifpQty === 0 || formData.serialNumbers.length < ifpQty;

    if (!serialValue) {
      message.error("No valid serial number detected");
      return;
    }
    if (isDuplicate) {
      message.error(`${serialValue} already added`);
      return;
    }
    if (!isWithinLimit) {
      message.error(`Cannot add more than ${ifpQty} serial numbers`);
      return;
    }

    // Store the scanned serial number and show device type selection
    setPendingSerialNumber(serialValue);
    setDeviceTypeModalVisible(true);
  };

  const handleDeviceTypeSelection = (deviceType) => {
    const qcChecks = deviceType === 'TV' ? tvQcChecks : ifpdQcChecks;
    const accessories = deviceType === 'TV' ? tvAccessories : ifpdAccessories;
    
    // Create default accessories object
    const defaultAccessories = {};
    accessories.forEach(accessory => {
      defaultAccessories[accessory.key] = true;
    });
    
    const deviceData = {
      serial: pendingSerialNumber,
      deviceType: deviceType,
      entryMethod: pendingSerialNumber ? 'scanned' : 'manual',
      productPhoto: productPhoto,
      installationStatus: 'installed', // Default to installed
      qcChecks: {
        deviceType: deviceType,
        checks: qcChecks.map(check => ({
          name: check,
          status: "passed",
          notes: ""
        }))
      },
      accessories: defaultAccessories
    };
    
    setFormData(prev => ({
      ...prev,
      serialNumbers: [...prev.serialNumbers, deviceData],
    }));
    
    setDeviceTypeModalVisible(false);
    setManualEntryModalVisible(false);
    setPendingSerialNumber("");
    setManualSerialNumber("");
    setProductPhoto(null);
    setPhotoPreview("");
    message.success(`${pendingSerialNumber || manualSerialNumber} added as ${deviceType}`);
  };

  const handleManualEntry = () => {
    if (!manualSerialNumber.trim()) {
      message.error("Please enter a serial number");
      return;
    }
    
    const isDuplicate = formData.serialNumbers.some(item =>
      typeof item === 'string' ? item === manualSerialNumber : item.serial === manualSerialNumber
    );
    
    if (isDuplicate) {
      message.error(`${manualSerialNumber} already added`);
      return;
    }
    
    setPendingSerialNumber(manualSerialNumber);
    setDeviceTypeModalVisible(true);
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        message.error("Photo size must be less than 5MB");
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target.result);
        setProductPhoto(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const updateInstallationStatus = (deviceIndex, status) => {
    const updated = [...formData.serialNumbers];
    updated[deviceIndex].installationStatus = status;
    setFormData({ ...formData, serialNumbers: updated });
    message.success(`Device ${updated[deviceIndex].serial} marked as ${status}`);
  };

  const handleQcComplete = () => {
    if (currentDeviceIndex === null) return;
    
    const device = formData.serialNumbers[currentDeviceIndex];
    const updatedChecks = device.qcChecks.checks.map((check, index) => ({
      ...check,
      status: failedItems.includes(index) ? "failed" : "passed"
    }));

    const updatedSerialNumbers = [...formData.serialNumbers];
    updatedSerialNumbers[currentDeviceIndex] = {
      ...device,
      qcChecks: {
        ...device.qcChecks,
        checks: updatedChecks
      }
    };

    setFormData({ ...formData, serialNumbers: updatedSerialNumbers });
    
    if (failedItems.length > 0) {
      message.warning(`${failedItems.length} item(s) marked as failed for device ${device.serial}`);
    } else {
      message.success(`All quality checks passed for device ${device.serial}`);
    }
    setQcModalVisible(false);
    setFailedItems([]);
    setCurrentDeviceIndex(null);
    setCurrentDeviceQcChecks([]);
  };

  const openQcModal = (deviceIndex) => {
    const device = formData.serialNumbers[deviceIndex];
    if (!device || !device.qcChecks) {
      message.error("Device QC data not found");
      return;
    }
    
    setCurrentDeviceIndex(deviceIndex);
    setCurrentDeviceQcChecks(device.qcChecks.checks);
    setFailedItems(
      device.qcChecks.checks
        .map((check, index) => check.status === "failed" ? index : -1)
        .filter(index => index !== -1)
    );
    setQcModalVisible(true);
  };

  const toggleFailedItem = (index) => {
    setFailedItems(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const updateDeviceAccessory = (deviceIndex, accessoryKey, checked) => {
    const updated = [...formData.serialNumbers];
    if (!updated[deviceIndex].accessories) {
      const device = updated[deviceIndex];
      const accessories = device.deviceType === 'TV' ? tvAccessories : ifpdAccessories;
      updated[deviceIndex].accessories = {};
      accessories.forEach(accessory => {
        updated[deviceIndex].accessories[accessory.key] = true;
      });
    }
    updated[deviceIndex].accessories[accessoryKey] = checked;
    setFormData({ ...formData, serialNumbers: updated });
  };

  const sendOtp = async () => {
    if (!formData.mobileNumber || formData.mobileNumber.length < 10) {
      message.error("Please enter a valid 10-digit mobile number");
      return;
    }
    setOtpState({ ...otpState, isLoading: true });
    
    try {
      const response = await fetch(`${process.env.REACT_APP_GREENFIELD_URL}/api/otp/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenState.token}`,
        },
        body: JSON.stringify({
          mobileNumber: formData.mobileNumber
        })
      });

      const result = await response.json();

      if (handleAuthError(response, result)) return;
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to send OTP');
      }

      setOtpState({
        isOtpSent: true,
        isOtpVerified: false,
        isLoading: false,
        countdown: 60
      });
      message.success(`Verification code sent to ${formData.mobileNumber}`);
      startCountdown();
    } catch (error) {
      console.error('Error sending OTP:', error);
      setOtpState({ ...otpState, isLoading: false });
      message.error(error.message || 'Failed to send OTP. Please try again.');
    }
  };

  const verifyOtp = async () => {
    if (!formData.otp || formData.otp.length !== 6) {
      message.error("Please enter a valid 6-digit OTP");
      return;
    }
    setOtpState({ ...otpState, isLoading: true });
    
    try {
      const response = await fetch(`${process.env.REACT_APP_GREENFIELD_URL}/api/otp/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenState.token}`,
        },
        body: JSON.stringify({
          mobileNumber: formData.mobileNumber,
          otp: formData.otp
        })
      });

      const result = await response.json();

      if (handleAuthError(response, result)) return;
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Invalid OTP');
      }

      setOtpState({ ...otpState, isOtpVerified: true, isLoading: false });
      message.success("Mobile number verified successfully");
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setOtpState({ ...otpState, isLoading: false });
      message.error(error.message || 'Invalid OTP. Please try again.');
    }
  };

  const startCountdown = () => {
    const timer = setInterval(() => {
      setOtpState((prev) => {
        if (prev.countdown <= 1) {
          clearInterval(timer);
          return { ...prev, countdown: 0 };
        }
        return { ...prev, countdown: prev.countdown - 1 };
      });
    }, 1000);
  };

  const resendOtp = () => {
    setOtpState({
      isOtpSent: false,
      isOtpVerified: false,
      isLoading: false,
      countdown: 0
    });
    setFormData({ ...formData, otp: "" });
    sendOtp();
  };

  // Validation function to check if all required fields are filled
  const isFormValid = () => {
    // Basic required fields
    if (!formData.installationDate) return false;
    if (!formData.salesOrderNo.trim()) return false;
    if (!schoolDataState.isFetched) return false;
    if (!formData.schoolName.trim()) return false;
    if (!formData.spocName.trim()) return false;
    if (!formData.schoolAddress.trim()) return false;
    if (!formData.totalIFPQty || parseInt(formData.totalIFPQty) === 0) return false;
    if (formData.serialNumbers.length === 0) return false;
    
    // Check that all devices have device type and installation status
    const incompleteDevices = formData.serialNumbers.filter(item => 
      typeof item === 'string' || !item.deviceType || !item.installationStatus
    );
    if (incompleteDevices.length > 0) return false;
    
    // Check for installed devices that need QC checks
    const installedDevices = formData.serialNumbers.filter(item => 
      typeof item === 'object' && item.installationStatus === 'installed'
    );
    
    const installedDevicesWithoutQc = installedDevices.filter(item => 
      !item.qcChecks || item.qcChecks.checks.some(check => check.status === 'pending')
    );
    
    if (installedDevicesWithoutQc.length > 0) return false;
    
    // Check for failed QC checks on installed devices
    const installedDevicesWithFailedQc = installedDevices.filter(item => 
      item.qcChecks && item.qcChecks.checks.some(check => check.status === 'failed')
    );
    
    if (installedDevicesWithFailedQc.length > 0) return false;
    
    // Engineer and contact information
    if (!formData.engineerName.trim()) return false;
    if (!formData.engineerContact.trim()) return false;
    if (!formData.schoolOwnerEmail.trim()) return false;
    if (!formData.selectedContact) return false;
    if (!formData.signaturePerson.trim()) return false;
    if (!formData.signatureRole.trim()) return false;
    if (!signatureState.isSigned) return false;
    if (!otpState.isOtpVerified) return false;
    if (!formData.agreement) return false;
    
    return true;
  };

  // Signature drawing functions
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      const touch = e.touches[0] || e.changedTouches[0];
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    setIsDrawing(true);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const coords = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const coords = getCoordinates(e);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const hasDrawing = imageData.data.some((value, index) => index % 4 === 3 && value > 0);
    
    if (!hasDrawing) {
      message.warning("Please draw your signature before saving");
      return;
    }
    const signatureData = canvas.toDataURL();
    setSignatureState({ isSigned: true, signatureData });
    setFormData({ ...formData, digitalSignature: signatureData });
    setSignatureModalVisible(false);
    message.success("Digital signature saved successfully");
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setSignatureState({ isSigned: false, signatureData: "" });
    setFormData({ ...formData, digitalSignature: "" });
    message.success("Signature cleared");
  };

  const handleSubmitReport = async () => {
    const linkToken = tokenState.token;
    const validationErrors = [];
    if (!formData.installationDate) validationErrors.push("Installation Date is required");
    if (!formData.salesOrderNo.trim()) validationErrors.push("Sales Order Number is required");
    if (!schoolDataState.isFetched) validationErrors.push("Please fetch school details first");
    if (!formData.schoolName.trim()) validationErrors.push("School Name is required");
    if (!formData.spocName.trim()) validationErrors.push("SPOC Name is required");
    if (!formData.schoolAddress.trim()) validationErrors.push("School Address is required");
    if (!formData.totalIFPQty || parseInt(formData.totalIFPQty) === 0) {
      validationErrors.push("Total IFP Quantity is required");
    }
    if (formData.serialNumbers.length === 0) {
      validationErrors.push("At least one serial number is required");
    }
    
    // Check that all devices have device type and installation status
    const incompleteDevices = formData.serialNumbers.filter(item => 
      typeof item === 'string' || !item.deviceType || !item.installationStatus
    );
    if (incompleteDevices.length > 0) {
      validationErrors.push("All devices must have device type selected and installation status confirmed");
    }
    
    // Check for installed devices that need QC checks
    const installedDevices = formData.serialNumbers.filter(item => 
      typeof item === 'object' && item.installationStatus === 'installed'
    );
    
    const installedDevicesWithoutQc = installedDevices.filter(item => 
      !item.qcChecks || item.qcChecks.checks.some(check => check.status === 'pending')
    );
    
    if (installedDevicesWithoutQc.length > 0) {
      validationErrors.push("All installed devices must have completed QC checks");
    }
    
    // Check for failed QC checks on installed devices
    const installedDevicesWithFailedQc = installedDevices.filter(item => 
      item.qcChecks && item.qcChecks.checks.some(check => check.status === 'failed')
    );
    
    if (installedDevicesWithFailedQc.length > 0) {
      const failedDevicesList = installedDevicesWithFailedQc.map(device => device.serial).join(', ');
      validationErrors.push(`Some installed devices have failed QC checks: ${failedDevicesList}. Please resolve or acknowledge these failures.`);
    }
    if (!formData.engineerName.trim()) validationErrors.push("Engineer Name is required");
    if (!formData.engineerContact.trim()) validationErrors.push("Engineer Contact is required");
    if (!formData.schoolOwnerEmail.trim()) validationErrors.push("School Owner Email is required");
    if (!formData.selectedContact) validationErrors.push("Please select a contact for signature and OTP verification");
    if (!formData.signaturePerson.trim()) validationErrors.push("Signature person name is required");
    if (!formData.signatureRole.trim()) validationErrors.push("Signature person role is required");
    if (!signatureState.isSigned) validationErrors.push("Digital signature is required");
    if (!otpState.isOtpVerified) validationErrors.push("Mobile number verification is required");
    if (!formData.agreement) validationErrors.push("Agreement acceptance is required");
    
    if (validationErrors.length > 0) {
      message.error(validationErrors[0]);
      return;
    }
    
    // Submit to backend
    try {
      setIsSubmitting(true);
      message.loading({ content: 'Submitting report...', key: 'submit' });
      
      const reportPayload = {
        ...formData,
        signatureData: signatureState.signatureData,
        submittedAt: new Date().toISOString(),
      };

      const response = await fetch(`${process.env.REACT_APP_GREENFIELD_URL}/api/installation-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${linkToken}`,
        },
        body: JSON.stringify(reportPayload)
      });

      const result = await response.json();

      if (handleAuthError(response, result)) return;
      if (response.ok && result.success) {
        message.success({
          content: `Report submitted successfully! Report ID: ${result.reportId}`,
          key: 'submit',
          duration: 5
        });
        
        // Clear saved data after successful submission
        clearSavedData();
        
        // Show report URL
        Modal.success({
          title: 'Report Submitted Successfully',
          content: (
            <div>
              <p>Your installation report has been saved and email sent.</p>
              <p><strong>Report ID:</strong> {result.reportId}</p>
              <p>You can view the report at:</p>
              <a 
                href={`/#/report-view/${result.reportId}${linkToken ? `?token=${linkToken}` : ''}`}
                target="_blank" 
                rel="noopener noreferrer"
              >
                View Report
              </a>
            </div>
          ),
          onOk: () => {
            window.location.reload();
          }
        });
      } else {
        throw new Error(result.message || 'Failed to submit report');
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      message.error({ 
        content: `Failed to submit report: ${error.message}`, 
        key: 'submit' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadReport = () => {
    const reportData = {
      ...formData,
      signatureData: signatureState.signatureData,
      timestamp: new Date().toISOString()
    };
    const dataStr = JSON.stringify(reportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `installation-report-${formData.salesOrderNo || 'draft'}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success("Report downloaded successfully");
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════

  if (tokenState.error === 'expired') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#f5f5f5',
        padding: '24px',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏰</div>
        <Title level={3} style={{ color: '#cf1322', marginBottom: 8 }}>Link Expired</Title>
        <Text type="secondary">This installation report link has expired. Please contact your OPP manager to generate a new link.</Text>
      </div>
    );
  }

  return (
    <>
      {/* Header with hamburger menu */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '60px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        zIndex: 1000,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {isLoggedIn && !tokenState.token && (
            <Button
              type="text"
              icon={<FaBars />}
              onClick={() => handleSetDrawerState(DRAWER_CONSTANTS.drawerState)}
              style={{
                color: 'white',
                fontSize: '18px',
                marginRight: '16px',
                border: 'none'
              }}
            />
          )}
          <Title level={3} style={{ color: 'white', margin: 0, fontWeight: 600 }}>
            Installation Report
          </Title>
        </div>
        {isLoggedIn && !tokenState.token && windowWidth >= 1000 && (
          <div style={{ display: 'flex', alignItems: 'center', color: 'white' }}>
            <span style={{ marginRight: '12px' }}>
              Welcome, <strong>{UserService.getUserData()?.name}</strong>
            </span>
            <Button
              type="text"
              onClick={handleLogOut}
              style={{ color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}
            >
              Logout
            </Button>
          </div>
        )}
      </div>

      {isLoggedIn && !tokenState.token && (
        <DrawerComponent
          onClose={() => handleSetDrawerState(DRAWER_CONSTANTS.drawerState)}
          isOpen={drawerState.isOpen}
          onClickMenuItem={handleSetDrawerState}
          handleLogOut={handleLogOut}
          isMobileDevice={windowWidth < 1000}
          menuItemDisabled={false}
          tableColumns={[]}
          handleCheckboxAction={() => {}}
        />
      )}

      <div className="installation-report-container" style={{ paddingTop: '60px' }}>
      {tokenState.isValidating && (
        <div style={{ padding: '16px 24px', background: '#e6f7ff', borderBottom: '1px solid #91d5ff', textAlign: 'center' }}>
          <Spin size="small" style={{ marginRight: 8 }} /> Validating link...
        </div>
      )}
      {/* Header */}
      <div className="report-header">
        <div className="report-header-content">
          <Space>
            <span style={{ fontSize: 24 }}>🛡️</span>
            <Title level={2} style={{ color: 'white', margin: 0, fontWeight: 600 }}>
              Installation & Service Report
            </Title>
          </Space>
        </div>
      </div>

      <div className="report-main-content">
        {/* Header Card */}
        <Card 
          style={{ marginBottom: 24, overflow: 'hidden' }}
          bodyStyle={{ padding: 0 }}
        >
          <div className="gradient-card-header">
            <Row justify="space-between" align="middle" gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Title level={2} style={{ color: 'white', margin: 0, fontWeight: 700 }}>
                  LEAD Group
                </Title>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ textAlign: 'right' }}>
                  <Text style={{ color: 'white', display: 'block', marginBottom: 8, fontSize: 14 }}>
                    Installation Date:
                  </Text>
                  <Input
                    type="date"
                    value={formData.installationDate || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      installationDate: e.target.value 
                    })}
                    style={{ width: '100%', maxWidth: 200 }}
                    placeholder="DD/MM/YYYY"
                    required
                  />
                </div>
              </Col>
            </Row>
            <Title level={3} style={{ color: 'white', marginTop: 24, marginBottom: 0, textAlign: 'center', fontWeight: 600 }}>
              INSTALLATION SIGN-OFF FORM
            </Title>
          </div>
        </Card>

        {/* School Information */}
        <Card 
          title={
            <Space>
              <span style={{ fontSize: 18 }}>🏫</span>
              <span>School Information</span>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text strong>Sales Order NO:</Text>
              <Space.Compact style={{ width: '100%', marginTop: 8 }}>
                <Input
                  value={formData.salesOrderNo}
                  onChange={(e) => setFormData({
                    ...formData,
                    salesOrderNo: e.target.value
                  })}
                  placeholder="Enter sales order number"
                  disabled={schoolDataState.isLoading || tokenState.isValidating || tokenState.isValid}
                  size="large"
                />
                <Button
                  type="primary"
                  onClick={fetchSchoolDetails}
                  disabled={schoolDataState.isLoading || !formData.salesOrderNo.trim() || tokenState.isValid}
                  loading={schoolDataState.isLoading || tokenState.isValidating}
                  icon={<FaSearch />}
                  size="large"
                >
                  Fetch
                </Button>
              </Space.Compact>
            </div>

            {schoolDataState.isFetched && (
              <div style={{
                padding: 20,
                background: '#f0fdf4',
                border: '1px solid #86efac',
                borderRadius: 8
              }}>
                <Space style={{ marginBottom: 16 }}>
                  <span style={{ fontSize: 18 }}>✅</span>
                  <Text strong style={{ color: '#16a34a', fontSize: 15 }}>
                    School details fetched successfully
                  </Text>
                </Space>

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Text strong>School Name:</Text>
                    <Input
                      value={formData.schoolName}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        schoolName: e.target.value 
                      })}
                      style={{ marginTop: 8 }}
                      disabled={true}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <Text strong>SPOC Name & Number:</Text>
                    <Input
                      value={formData.spocName}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        spocName: e.target.value 
                      })}
                      style={{ marginTop: 8 }}
                      placeholder="Enter SPOC name and number"
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <Text strong>Total TV & IFPD Installed Qty:</Text>
                    <Input
                      type="number"
                      value={formData.totalIFPQty}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        totalIFPQty: e.target.value 
                      })}
                      style={{ marginTop: 8 }}
                      disabled={true}
                    />
                  </Col>
                  {/* <Col xs={24} md={12}>
                    <Text strong>Brand Name:</Text>
                    <Input
                      value={formData.brandName}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        brandName: e.target.value 
                      })}
                      placeholder="e.g., Hyundai, Xentec, Vaama"
                      style={{ marginTop: 8 }}
                    />
                  </Col> */}
                  <Col xs={24}>
                    <Text strong>School Address:</Text>
                    <TextArea
                      value={formData.schoolAddress}
                      onChange={(e) => setFormData({
                        ...formData,
                        schoolAddress: e.target.value
                      })}
                      rows={3}
                      style={{ marginTop: 8 }}
                      placeholder="Enter complete school address"
                      disabled={tokenState.isValid}
                    />
                  </Col>
                </Row>
              </div>
            )}

            {!schoolDataState.isFetched && !schoolDataState.isLoading && (
              <div style={{
                textAlign: 'center',
                padding: 32,
                border: '2px dashed #d9d9d9',
                borderRadius: 8,
                background: '#fafafa'
              }}>
                <FaSearch size={48} style={{ color: '#bfbfbf', marginBottom: 16 }} />
                <Paragraph type="secondary">
                  Enter sales order number and click "Fetch" to retrieve school information
                </Paragraph>
              </div>
            )}
          </Space>
        </Card>

        {/* TV & IFPD Serial Numbers */}
        <Card
          title={
            <Space>
              <span style={{ fontSize: 18 }}>📱</span>
              <span>
                TV & IFPD Serial Numbers ({formData.serialNumbers.length}/
                {parseInt(formData.totalIFPQty) || 0})
              </span>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div style={{
              background: '#fefce8',
              border: '1px solid #fde047',
              borderRadius: 8,
              padding: 24,
              textAlign: 'center'
            }}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space>
                  <span style={{ fontSize: 32 }}>📱</span>
                  <Text strong style={{ fontSize: 16, color: '#ca8a04' }}>
                    Add TV & IFPD Serial Numbers
                  </Text>
                </Space>
                <Paragraph style={{ color: '#ca8a04', margin: 0, fontSize: 14 }}>
                  Choose scanning for accuracy or manual entry with photo attachment
                </Paragraph>
              </Space>
            </div>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Button
                  type="primary"
                  size="large"
                  icon={<span style={{ fontSize: 18, marginRight: 8 }}>📷</span>}
                  onClick={() => setScannerVisible(true)}
                  block
                >
                  Scan Serial Number Barcode
                </Button>
              </Col>
              <Col xs={24} sm={12}>
                <Button
                  type="primary"
                  size="large"
                  icon={<span style={{ fontSize: 18, marginRight: 8 }}>✏️</span>}
                  onClick={() => setManualEntryModalVisible(true)}
                  block
                >
                  Manual Entry + Photo
                </Button>
              </Col>
            </Row>

            {formData.serialNumbers.length > 0 && (
              <div>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>
                  Added Serial Numbers:
                </Text>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {formData.serialNumbers.map((item, index) => (
                    <Card
                      key={index}
                      size="small"
                      style={{ background: '#fafafa' }}
                    >
                      <Row gutter={[8, 8]} align="middle">
                        <Col flex="auto">
                          <Space>
                            <Tag color="blue">#{index + 1}</Tag>
                            <Text strong>
                              {typeof item === 'string' ? item : item.serial}
                            </Text>
                            {typeof item === 'object' && item.deviceType && (
                              <Tag color={item.deviceType === 'TV' ? 'green' : 'purple'}>
                                {item.deviceType}
                              </Tag>
                            )}
                            {typeof item === 'object' && item.entryMethod && (
                              <Tag color={item.entryMethod === 'scanned' ? 'blue' : 'orange'}>
                                {item.entryMethod === 'scanned' ? '📷 Scanned' : '✏️ Manual'}
                              </Tag>
                            )}
                            {typeof item === 'object' && item.installationStatus && (
                              <Tag color={
                                item.installationStatus === 'installed' ? 'success' : 'error'
                              }>
                                {item.installationStatus === 'installed' ? '✅ Installed' : '❌ Not Installed'}
                              </Tag>
                            )}
                          </Space>
                        </Col>
                        <Col>
                          <Button
                            type="text"
                            danger
                            icon={<FaTimes />}
                            onClick={() => removeSerialNumber(index)}
                            size="small"
                          />
                        </Col>
                      </Row>

                      {typeof item === 'object' && item.productPhoto && (
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Product Photo:</Text>
                          <div style={{ marginTop: 4 }}>
                            <img 
                              src={item.productPhoto} 
                              alt="Product" 
                              style={{ 
                                maxWidth: '100px', 
                                maxHeight: '100px', 
                                borderRadius: 4,
                                border: '1px solid #d9d9d9'
                              }} 
                            />
                          </div>
                        </div>
                      )}

                      {/* Installation Status Controls */}
                      <div style={{ marginTop: 8 }}>
                        <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                          Installation Status:
                        </Text>
                        <Row gutter={[4, 4]}>
                          <Col xs={12}>
                            <Button
                              size="small"
                              type={item.installationStatus === 'installed' ? 'primary' : 'default'}
                              onClick={() => updateInstallationStatus(index, 'installed')}
                              block
                              style={{ fontSize: 10 }}
                            >
                              ✅ Installed
                            </Button>
                          </Col>
                          <Col xs={12}>
                            <Button
                              size="small"
                              type={item.installationStatus === 'not-installed' ? 'primary' : 'default'}
                              onClick={() => updateInstallationStatus(index, 'not-installed')}
                              block
                              style={{ fontSize: 10 }}
                            >
                              ❌ Not Installed
                            </Button>
                          </Col>
                        </Row>
                      </div>

                      <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                        <Col xs={24} sm={12}>
                          <Button
                            size="small"
                            icon={<FaCheckCircle />}
                            onClick={() => openQcModal(index)}
                            block
                            disabled={typeof item === 'string' || !item.qcChecks || item.installationStatus !== 'installed'}
                          >
                            QC Check ({item.deviceType || 'Unknown'})
                          </Button>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Button
                            size="small"
                            icon={<FaBox />}
                            onClick={() => {
                              setCurrentDeviceIndex(index);
                              setAccessoriesModalVisible(true);
                            }}
                            block
                            disabled={item.installationStatus !== 'installed'}
                          >
                            Accessories
                          </Button>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </Space>
              </div>
            )}
          </Space>
        </Card>

        {/* Installation Details */}
        {/* <Card
          title={
            <Space>
              <span style={{ fontSize: 18 }}>🔧</span>
              <span>Installation Details</span>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Text strong>UPS Present:</Text>
                <Radio.Group
                  value={formData.upsPresent}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    upsPresent: e.target.value 
                  })}
                  style={{ marginTop: 8, display: 'block' }}
                >
                  <Space>
                    <Radio value="yes">Yes</Radio>
                    <Radio value="no">No</Radio>
                  </Space>
                </Radio.Group>
              </Col>
              <Col xs={24} md={12}>
                <Text strong>Earthing Work Required:</Text>
                <Radio.Group
                  value={formData.earthingRequired}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    earthingRequired: e.target.value 
                  })}
                  style={{ marginTop: 8, display: 'block' }}
                >
                  <Space>
                    <Radio value="yes">Yes</Radio>
                    <Radio value="no">No</Radio>
                  </Space>
                </Radio.Group>
              </Col>
              <Col xs={24}>
                <Text strong>Installation done on:</Text>
                <Radio.Group
                  value={formData.installationType}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    installationType: e.target.value 
                  })}
                  style={{ marginTop: 8, display: 'block' }}
                >
                  <Space direction="vertical">
                    <Radio value="wall-mount">Wall Mount</Radio>
                    <Radio value="movable-stand">Movable Stand</Radio>
                  </Space>
                </Radio.Group>
              </Col>
            </Row>
          </Space>
        </Card> */}

        {/* Engineer Information */}
        <Card
          title={
            <Space>
              <span style={{ fontSize: 18 }}>👷</span>
              <span>Engineer Information</span>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Text strong>Engineer Name:</Text>
              <Input
                value={formData.engineerName}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  engineerName: e.target.value 
                })}
                placeholder="Enter engineer name"
                style={{ marginTop: 8 }}
              />
            </Col>
            <Col xs={24} md={12}>
              <Text strong>Engineer Contact Number:</Text>
              <Input
                value={formData.engineerContact}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  engineerContact: e.target.value 
                })}
                placeholder="Enter contact number"
                style={{ marginTop: 8 }}
              />
            </Col>
          </Row>
        </Card>

        {/* School Owner/Principal/SPOC Information */}
        <Card
          title="School Owner / Principal / SPOC Information"
          style={{ marginBottom: 24 }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Text strong>Email Address:</Text>
              <Input
                type="email"
                value={formData.schoolOwnerEmail}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  schoolOwnerEmail: e.target.value 
                })}
                placeholder="Enter email address to receive report copy"
                style={{ marginTop: 8 }}
              />
              <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                A copy of this installation report will be sent to this email address
              </Text>
            </div>

            {/* Digital Signature */}
            <div>
              <Space style={{ marginBottom: 12 }}>
                <span style={{ fontSize: 18 }}>✍️</span>
                <Text strong style={{ fontSize: 15 }}>Digital Signature (Required)</Text>
              </Space>

              {/* Contact Selection Dropdown */}
              {formData.contacts && formData.contacts.length > 0 ? (
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Select Contact for Signature & OTP:</Text>
                  <Select
                    value={formData.selectedContact?.email}
                    onChange={handleContactSelection}
                    placeholder="Choose a contact for signature and OTP verification"
                    style={{ width: '100%', marginTop: 8 }}
                    size="large"
                    className="contact-selection-dropdown"
                  >
                    {formData.contacts.map((contact) => (
                      <Select.Option key={contact.email} value={contact.email}>
                        <div style={{ padding: '4px 0' }}>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>
                            {contact.name}
                          </div>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              ) : (
                <div style={{
                  padding: 16,
                  background: '#fff7e6',
                  border: '1px solid #ffd591',
                  borderRadius: 8,
                  marginBottom: 16,
                  textAlign: 'center'
                }}>
                  <Text type="secondary">
                    Please fetch school details first to load available contacts
                  </Text>
                </div>
              )}

              {/* Show selected contact details */}
              {formData.selectedContact && (
                <div className="contact-details-box">
                  <Text strong style={{ display: 'block', marginBottom: 12, color: '#0369a1' }}>
                    Selected Contact Details:
                  </Text>
                  <Row gutter={[12, 12]}>
                    <Col xs={24} sm={12}>
                      <div>
                        <Text strong style={{ fontSize: 12, color: '#374151' }}>Name:</Text>
                        <div className="contact-field-display" style={{ marginTop: 4 }}>
                          {formData.signaturePerson}
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div>
                        <Text strong style={{ fontSize: 12, color: '#374151' }}>Role:</Text>
                        <div className="contact-field-display" style={{ marginTop: 4 }}>
                          {formData.signatureRole}
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <div style={{ 
                    marginTop: 8, 
                    fontSize: 11, 
                    color: '#6b7280',
                    fontStyle: 'italic'
                  }}>
                  </div>
                </div>
              )}

              {signatureState.isSigned ? (
                <div>
                  <div style={{
                    padding: 16,
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    borderRadius: 8,
                    marginBottom: 16
                  }}>
                    <Row justify="space-between" align="middle">
                      <Col>
                        <Space>
                          <FaCheckCircle style={{ color: '#52c41a' }} />
                          <Text strong style={{ color: '#52c41a' }}>
                            Signature captured successfully
                          </Text>
                        </Space>
                      </Col>
                      <Col>
                        <Button
                          size="small"
                          icon={<FaPen />}
                          onClick={() => setSignatureModalVisible(true)}
                        >
                          Edit
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  <div style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: 8,
                    padding: 16,
                    background: 'white'
                  }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Signature Preview:
                    </Text>
                    <div style={{
                      border: '1px solid #f0f0f0',
                      borderRadius: 4,
                      background: '#fafafa',
                      padding: 8,
                      textAlign: 'center'
                    }}>
                      <img
                        src={signatureState.signatureData}
                        alt="Digital Signature"
                        style={{ maxWidth: '100%', maxHeight: 100 }}
                      />
                    </div>
                    <div style={{ textAlign: 'center', marginTop: 8 }}>
                      <Button
                        size="small"
                        danger
                        icon={<FaUndo />}
                        onClick={clearSignature}
                      >
                        Remove Signature
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: 32,
                  border: '2px dashed #d9d9d9',
                  borderRadius: 8,
                  background: '#fafafa'
                }}>
                  <FaPen size={48} style={{ color: '#bfbfbf', marginBottom: 16 }} />
                  <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    No signature added yet
                  </Paragraph>
                  <Button
                    type="primary"
                    icon={<FaPen />}
                    onClick={() => setSignatureModalVisible(true)}
                  >
                    Add Digital Signature
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile OTP Verification */}
            <Divider />
            <div>
              <Space style={{ marginBottom: 16 }}>
                <span style={{ fontSize: 18 }}>🛡️</span>
                <Text strong style={{ fontSize: 15 }}>Mobile OTP Verification</Text>
              </Space>

              {formData.selectedContact ? (
                <div>
                  <div className="otp-info-box">
                    <Text strong style={{ fontSize: 13, color: '#0369a1' }}>
                      OTP will be sent to: {formData.selectedContact.name}
                    </Text>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                      Phone: {formData.mobileNumber}
                    </div>
                    <div style={{marginTop: 4}}>
                    <Button
                          type="primary"
                          onClick={sendOtp}
                          disabled={
                            otpState.isLoading ||
                            otpState.countdown > 0 ||
                            otpState.isOtpVerified ||
                            !formData.mobileNumber ||
                            formData.mobileNumber.length < 10
                          }
                          loading={otpState.isLoading}
                          icon={<FaPhone />}
                          style={{ minWidth: 80 }}
                        >
                          {otpState.countdown > 0
                            ? `${otpState.countdown}s`
                            : otpState.isOtpSent
                            ? "Resend"
                            : "Send OTP"}
                        </Button>
                        {otpState.isOtpSent && !otpState.isOtpVerified && (
                          <Col xs={24} sm={12}>
                            <Text strong style={{ fontSize: 13 }}>Enter OTP:</Text>
                            <Space.Compact style={{ width: '100%', marginTop: 8 }}>
                              <Input
                                placeholder="Enter 6-digit OTP"
                                value={formData.otp}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                  setFormData({ ...formData, otp: value });
                                }}
                                maxLength={6}
                                style={{ fontSize: 14 }}
                              />
                              <Button
                                type="primary"
                                onClick={verifyOtp}
                                disabled={otpState.isLoading || formData.otp.length !== 6}
                                loading={otpState.isLoading}
                                icon={<FaShieldAlt />}
                                style={{ minWidth: 80 }}
                              >
                                Verify
                              </Button>
                            </Space.Compact>
                          </Col>
                        )}
                      </div>
                  </div>
                </div>
              ) : (
                <div style={{
                  padding: 16,
                  background: '#fff7e6',
                  border: '1px solid #ffd591',
                  borderRadius: 8,
                  textAlign: 'center'
                }}>
                  <Text type="secondary">
                    Please select a contact above to enable OTP verification
                  </Text>
                </div>
              )}

              {otpState.isOtpVerified && (
                <div style={{
                  marginTop: 16,
                  padding: 12,
                  background: '#f6ffed',
                  border: '1px solid #b7eb8f',
                  borderRadius: 8
                }}>
                  <Space>
                    <FaShieldAlt style={{ color: '#52c41a' }} />
                    <Text strong style={{ color: '#52c41a', fontSize: 14 }}>
                      Mobile number verified successfully for {formData.selectedContact?.name}
                    </Text>
                  </Space>
                </div>
              )}

              {otpState.countdown > 0 && (
                <div style={{ marginTop: 12, textAlign: 'center' }}>
                  <Button
                    type="link"
                    onClick={resendOtp}
                    style={{ padding: 0, fontSize: 12 }}
                  >
                    Didn't receive OTP? Click to resend
                  </Button>
                </div>
              )}
            </div>

            <Divider />

            <Checkbox
              checked={formData.agreement}
              onChange={(e) => setFormData({ 
                ...formData, 
                agreement: e.target.checked 
              })}
            >
              <Text style={{ fontSize: 14 }}>
                I hereby acknowledge the above and confirm my agreement to Lead School's 
                Terms of Use as outlined in the detailed agreement signed.
              </Text>
            </Checkbox>
          </Space>
        </Card>

        {/* Action Buttons */}
        <Row gutter={[16, 16]} style={{ marginTop: 24, marginBottom: 24 }}>
          {!isFormValid() && (
            <Col xs={24}>
              <div style={{
                padding: 16,
                background: '#fff7e6',
                border: '1px solid #ffd591',
                borderRadius: 8,
                marginBottom: 16,
                textAlign: 'center'
              }}>
                <Space>
                  <span style={{ fontSize: 18 }}>⚠️</span>
                  <Text strong style={{ color: '#fa8c16' }}>
                    Please complete all required fields and validations to enable download and submit options
                  </Text>
                </Space>
              </div>
            </Col>
          )}
          <Col xs={24} sm={12}>
            <Tooltip 
              title={!isFormValid() ? "Please complete all required fields and validations before downloading" : ""}
            >
              <Button
                size="large"
                icon={<FaDownload />}
                onClick={handleDownloadReport}
                disabled={!isFormValid()}
                block
              >
                Download Report
              </Button>
            </Tooltip>
          </Col>
          <Col xs={24} sm={12}>
            <Tooltip 
              title={!isFormValid() ? "Please complete all required fields and validations before submitting" : ""}
            >
              <Button
                type="primary"
                size="large"
                icon={<FaCalendar />}
                onClick={handleSubmitReport}
                loading={isSubmitting}
                disabled={isSubmitting || !isFormValid()}
                block
              >
                {isSubmitting ? 'Submitting...' : 'Submit Report'}
              </Button>
            </Tooltip>
          </Col>
        </Row>
      </div>

      {/* Manual Entry Modal */}
      <Modal
        title={
          <Space>
            <span style={{ fontSize: 18 }}>✏️</span>
            <span>Manual Serial Number Entry</span>
          </Space>
        }
        open={manualEntryModalVisible}
        onCancel={() => {
          setManualEntryModalVisible(false);
          setManualSerialNumber("");
          setProductPhoto(null);
          setPhotoPreview("");
        }}
        footer={null}
        width={500}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div style={{
            padding: 16,
            background: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: 8
          }}>
            <Text strong style={{ color: '#fa8c16' }}>📸 Photo Required</Text>
            <div style={{ marginTop: 8, fontSize: 14, color: '#fa8c16' }}>
              When entering serial numbers manually, a photo of the packaged product is required for verification.
            </div>
          </div>
          
          <div>
            <Text strong>Serial Number:</Text>
            <Input
              value={manualSerialNumber}
              onChange={(e) => setManualSerialNumber(e.target.value)}
              placeholder="Enter device serial number"
              style={{ marginTop: 8 }}
              size="large"
            />
          </div>

          <div>
            <Text strong>Product Photo:</Text>
            <div style={{ marginTop: 8 }}>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
                id="photo-upload"
              />
              <Button
                onClick={() => document.getElementById('photo-upload').click()}
                icon={<span>📁</span>}
                size="large"
                block
                style={{ marginBottom: 16 }}
              >
                {productPhoto ? 'Change Photo' : 'Upload Photo'}
              </Button>
              
              {photoPreview && (
                <div style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  padding: 8,
                  textAlign: 'center'
                }}>
                  <img
                    src={photoPreview}
                    alt="Product preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      borderRadius: 4
                    }}
                  />
                  <div style={{ marginTop: 8 }}>
                    <Button
                      size="small"
                      danger
                      onClick={() => {
                        setProductPhoto(null);
                        setPhotoPreview("");
                      }}
                    >
                      Remove Photo
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Button
                onClick={() => {
                  setManualEntryModalVisible(false);
                  setManualSerialNumber("");
                  setProductPhoto(null);
                  setPhotoPreview("");
                }}
                block
              >
                Cancel
              </Button>
            </Col>
            <Col xs={24} sm={12}>
              <Button
                type="primary"
                onClick={handleManualEntry}
                disabled={!manualSerialNumber.trim() || !productPhoto}
                block
              >
                Add Device
              </Button>
            </Col>
          </Row>
        </Space>
      </Modal>

      {/* Device Type Selection Modal */}
      <Modal
        title={
          <Space>
            <span style={{ fontSize: 18 }}>📱</span>
            <span>Select Device Type</span>
          </Space>
        }
        open={deviceTypeModalVisible}
        onCancel={() => {
          setDeviceTypeModalVisible(false);
          setPendingSerialNumber("");
        }}
        footer={null}
        width={400}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div style={{
            padding: 16,
            background: '#f0f9ff',
            border: '1px solid #7dd3fc',
            borderRadius: 8
          }}>
            <Text strong>Scanned Serial Number:</Text>
            <div style={{ marginTop: 8, fontSize: 16, fontWeight: 600 }}>
              {pendingSerialNumber}
            </div>
          </div>
          
          <div>
            <Text strong style={{ display: 'block', marginBottom: 16 }}>
              Please select the device type:
            </Text>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Button
                size="large"
                block
                onClick={() => handleDeviceTypeSelection('TV')}
                style={{
                  height: 60,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Space>
                  <span style={{ fontSize: 24 }}>📺</span>
                  <span style={{ fontSize: 16, fontWeight: 600 }}>TV</span>
                </Space>
              </Button>
              <Button
                size="large"
                block
                onClick={() => handleDeviceTypeSelection('IFPD')}
                style={{
                  height: 60,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Space>
                  <span style={{ fontSize: 24 }}>🖥️</span>
                  <span style={{ fontSize: 16, fontWeight: 600 }}>IFPD (Interactive Flat Panel)</span>
                </Space>
              </Button>
            </Space>
          </div>
        </Space>
      </Modal>

      {/* Barcode Scanner Modal */}
      <BarcodeScanner
        visible={scannerVisible}
        onScan={handleScanResult}
        onClose={() => setScannerVisible(false)}
      />

      {/* Signature Modal */}
      <Modal
        title={
          <Space>
            <FaPen />
            <span>{signatureState.isSigned ? 'Edit' : 'Add'} Digital Signature</span>
          </Space>
        }
        open={signatureModalVisible}
        onCancel={() => setSignatureModalVisible(false)}
        footer={null}
        width="90%"
        style={{ maxWidth: 700 }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div style={{
            border: '2px dashed #d9d9d9',
            borderRadius: 8,
            padding: 24,
            background: '#fafafa'
          }}>
            <canvas
              ref={canvasRef}
              width={600}
              height={200}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              className="signature-canvas"
            />
            <Text type="secondary" style={{ 
              fontSize: 12, 
              marginTop: 8, 
              display: 'block', 
              textAlign: 'center' 
            }}>
              Sign above using your mouse, stylus, or finger
            </Text>
          </div>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Button
                icon={<FaUndo />}
                onClick={clearSignature}
                block
              >
                Clear
              </Button>
            </Col>
            <Col xs={24} sm={12}>
              <Button
                type="primary"
                icon={<FaCheckCircle />}
                onClick={saveSignature}
                block
              >
                Save Signature
              </Button>
            </Col>
          </Row>
        </Space>
      </Modal>

      {/* QC Modal */}
      <Modal
        title={
          <Space>
            <FaCheckCircle />
            <span>
              {currentDeviceIndex !== null && formData.serialNumbers[currentDeviceIndex] 
                ? `${formData.serialNumbers[currentDeviceIndex].deviceType || 'Device'} Quality Check`
                : 'Quality Check'
              }
            </span>
          </Space>
        }
        open={qcModalVisible}
        onCancel={() => {
          setQcModalVisible(false);
          setFailedItems([]);
          setCurrentDeviceIndex(null);
          setCurrentDeviceQcChecks([]);
        }}
        footer={null}
        width="90%"
        style={{ maxWidth: 800 }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {currentDeviceIndex !== null && formData.serialNumbers[currentDeviceIndex] && (
            <div style={{
              padding: 16,
              background: '#f0f9ff',
              border: '1px solid #7dd3fc',
              borderRadius: 8
            }}>
              <Text strong>Device: </Text>
              <Text>{formData.serialNumbers[currentDeviceIndex].serial}</Text>
              <Tag color={formData.serialNumbers[currentDeviceIndex].deviceType === 'TV' ? 'green' : 'purple'} style={{ marginLeft: 8 }}>
                {formData.serialNumbers[currentDeviceIndex].deviceType}
              </Tag>
            </div>
          )}

          <div style={{
            padding: 16,
            border: '1px solid #d9d9d9',
            borderRadius: 8,
            background: failedItems.length === 0 ? '#f6ffed' : '#fff2e8'
          }}>
            {failedItems.length === 0 ? (
              <Space>
                <Text style={{ fontSize: 24 }}>✅</Text>
                <Text strong style={{ fontSize: 18, color: '#52c41a' }}>
                  QC Passed
                </Text>
              </Space>
            ) : (
              <Space direction="vertical" size="small">
                <Space>
                  <Text style={{ fontSize: 24 }}>❌</Text>
                  <Text strong style={{ fontSize: 18, color: '#fa8c16' }}>
                    QC Failed
                  </Text>
                </Space>
                <div style={{
                  background: '#fff7e6',
                  padding: 12,
                  borderRadius: 4,
                  border: '1px solid #ffd591'
                }}>
                  <Text strong style={{ color: '#fa8c16', display: 'block', marginBottom: 8 }}>
                    Failed Items:
                  </Text>
                  <ul style={{ margin: 0, paddingLeft: 20, color: '#fa8c16' }}>
                    {failedItems.map(index => (
                      <li key={index}>{currentDeviceQcChecks[index]?.name}</li>
                    ))}
                  </ul>
                </div>
              </Space>
            )}
          </div>

          <div style={{
            padding: 16,
            background: '#fafafa',
            borderRadius: 8
          }}>
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              Please perform the following quality checks on the installed device:
            </Paragraph>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {currentDeviceQcChecks.map((check, index) => (
                <div
                  key={index}
                  style={{
                    padding: 12,
                    borderRadius: 4,
                    border: '1px solid #d9d9d9',
                    background: failedItems.includes(index) ? '#fff2e8' : 'white'
                  }}
                >
                  <Row justify="space-between" align="middle">
                    <Col flex="auto">
                      <Space>
                        <Text style={{ color: '#1890ff' }}>★</Text>
                        <Text strong>{check.name}</Text>
                      </Space>
                    </Col>
                    <Col>
                      <Space>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {failedItems.includes(index) ? 'Failed' : 'Passed'}
                        </Text>
                        <Checkbox
                          checked={!failedItems.includes(index)}
                          onChange={() => toggleFailedItem(index)}
                        />
                      </Space>
                    </Col>
                  </Row>
                </div>
              ))}
            </Space>
          </div>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Button
                onClick={() => {
                  setQcModalVisible(false);
                  setFailedItems([]);
                  setCurrentDeviceIndex(null);
                  setCurrentDeviceQcChecks([]);
                }}
                block
              >
                Cancel
              </Button>
            </Col>
            <Col xs={24} sm={12}>
              <Button
                type="primary"
                onClick={handleQcComplete}
                block
              >
                Submit QC Results
              </Button>
            </Col>
          </Row>
        </Space>
      </Modal>

      {/* Accessories Modal */}
      <Modal
        title={
          <Space>
            <FaBox />
            <span>Accessories Received</span>
          </Space>
        }
        open={accessoriesModalVisible}
        onCancel={() => {
          setAccessoriesModalVisible(false);
          setCurrentDeviceIndex(null);
        }}
        footer={null}
        width="90%"
        style={{ maxWidth: 500 }}
      >
        {currentDeviceIndex !== null && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Text type="secondary">
              Device: {typeof formData.serialNumbers[currentDeviceIndex] === 'string'
                ? formData.serialNumbers[currentDeviceIndex]
                : formData.serialNumbers[currentDeviceIndex].serial}
            </Text>

            {(() => {
              const device = formData.serialNumbers[currentDeviceIndex];
              const deviceType = device?.deviceType;
              const accessories = deviceType === 'TV' ? tvAccessories : ifpdAccessories;
              const deviceAccessories = formData.serialNumbers[currentDeviceIndex]?.accessories || {};
              const checkedAccessories = accessories.filter(acc => deviceAccessories[acc.key]);
              const missingAccessories = accessories.filter(acc => !deviceAccessories[acc.key]);
              const allReceived = checkedAccessories.length === accessories.length;

              return (
                <>
                  <div style={{
                    padding: 16,
                    border: '1px solid #d9d9d9',
                    borderRadius: 8,
                    background: allReceived ? '#f6ffed' : '#fff2e8'
                  }}>
                    {allReceived ? (
                      <Space>
                        <Text style={{ fontSize: 24 }}>✅</Text>
                        <Text strong style={{ fontSize: 18, color: '#52c41a' }}>
                          All Accessories Received
                        </Text>
                      </Space>
                    ) : (
                      <Space direction="vertical" size="small">
                        <Space>
                          <Text style={{ fontSize: 24 }}>❌</Text>
                          <Text strong style={{ fontSize: 18, color: '#fa8c16' }}>
                            Accessory Missing
                          </Text>
                        </Space>
                        {missingAccessories.length > 0 && (
                          <div style={{
                            background: '#fff7e6',
                            padding: 12,
                            borderRadius: 4,
                            border: '1px solid #ffd591'
                          }}>
                            <Text strong style={{ color: '#fa8c16', display: 'block', marginBottom: 8 }}>
                              Missing Accessories:
                            </Text>
                            <ul style={{ margin: 0, paddingLeft: 20, color: '#fa8c16' }}>
                              {missingAccessories.map(acc => (
                                <li key={acc.key}>{acc.label}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </Space>
                    )}
                  </div>

                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    {accessories.map(({ key, label }) => {
                      const isChecked = deviceAccessories[key] || false;
                      return (
                        <div
                          key={key}
                          style={{
                            padding: 12,
                            borderRadius: 4,
                            border: '1px solid #d9d9d9',
                            background: isChecked ? '#f6ffed' : '#fff2e8'
                          }}
                        >
                          <Row justify="space-between" align="middle">
                            <Col>
                              <Text strong>{label}</Text>
                            </Col>
                            <Col>
                              <Space>
                                <Tag color={isChecked ? 'success' : 'error'}>
                                  {isChecked ? 'Received' : 'Missing'}
                                </Tag>
                                <Checkbox
                                  checked={isChecked}
                                  onChange={(e) => updateDeviceAccessory(
                                    currentDeviceIndex,
                                    key,
                                    e.target.checked
                                  )}
                                />
                              </Space>
                            </Col>
                          </Row>
                        </div>
                      );
                    })}
                  </Space>
                </>
              );
            })()}

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Button
                  onClick={() => {
                    setAccessoriesModalVisible(false);
                    setCurrentDeviceIndex(null);
                  }}
                  block
                >
                  Cancel
                </Button>
              </Col>
              <Col xs={24} sm={12}>
                <Button
                  type="primary"
                  onClick={() => {
                    setAccessoriesModalVisible(false);
                    setCurrentDeviceIndex(null);
                    message.success("Accessories status updated");
                  }}
                  block
                >
                  Submit
                </Button>
              </Col>
            </Row>
          </Space>
        )}
      </Modal>
      </div>
    </>
  );
};

export default InstallationReportStandalone;
